import React, { useState } from "react";
import { Settlement, Merchant } from "@/shared/api/entities";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Badge } from "@/shared/components/ui/badge";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Plus,
  Search,
  MoreHorizontal,
  FileText,
  Download,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  Eye,
  Send,
} from "lucide-react";
import { format } from "date-fns";

const statusConfig = {
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700" },
  processing: { icon: Loader2, color: "bg-blue-100 text-blue-700" },
  completed: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
  failed: { icon: XCircle, color: "bg-red-100 text-red-700" },
};

export default function Settlements() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const { t } = useLanguage();

  const queryClient = useQueryClient();

  const { data: settlements = [], isLoading } = useQuery({
    queryKey: ["settlements"],
    queryFn: () => Settlement.list("-created_date"),
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => Merchant.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => Settlement.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["settlements"]);
      setCreateOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => Settlement.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["settlements"]);
    },
  });

  const filteredSettlements = settlements.filter((s) => {
    if (search) {
      const searchLower = search.toLowerCase();
      if (
        !s.settlement_id?.toLowerCase().includes(searchLower) &&
        !s.merchant_code?.toLowerCase().includes(searchLower)
      ) {
        return false;
      }
    }
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    return true;
  });

  const handleProcess = async (settlement) => {
    await updateMutation.mutateAsync({
      id: settlement.id,
      data: { ...settlement, status: "processing" },
    });
  };

  const handleComplete = async (settlement) => {
    await updateMutation.mutateAsync({
      id: settlement.id,
      data: { ...settlement, status: "completed", processed_at: new Date().toISOString() },
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("settlementTitle")}</h1>
          <p className="text-slate-500">{t("merchantPayoutManagement")}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          {t("createSettlement")}
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={t("searchSettlements")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder={t("status")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allStatus")}</SelectItem>
              <SelectItem value="pending">{t("pending")}</SelectItem>
              <SelectItem value="processing">{t("processing")}</SelectItem>
              <SelectItem value="completed">{t("completed")}</SelectItem>
              <SelectItem value="failed">{t("failed")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">{t("settlementId")}</TableHead>
              <TableHead className="font-semibold">{t("merchant")}</TableHead>
              <TableHead className="font-semibold">{t("period")}</TableHead>
              <TableHead className="font-semibold text-right">{t("deposits")}</TableHead>
              <TableHead className="font-semibold text-right">{t("withdrawals")}</TableHead>
              <TableHead className="font-semibold text-right">{t("netAmount")}</TableHead>
              <TableHead className="font-semibold">{t("status")}</TableHead>
              <TableHead className="font-semibold w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredSettlements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                  {isLoading ? t("loading") : t("noSettlements")}
                </TableCell>
              </TableRow>
            ) : (
              filteredSettlements.map((settlement, index) => {
                const status = statusConfig[settlement.status] || statusConfig.pending;
                const StatusIcon = status.icon;

                return (
                  <motion.tr
                    key={settlement.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.02 * index }}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-purple-600" />
                        </div>
                        <span className="font-mono text-sm">{settlement.settlement_id}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{settlement.merchant_code}</span>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div>{settlement.period_start ? format(new Date(settlement.period_start), "MMM d") : "N/A"}</div>
                        <div className="text-slate-500">
                          to {settlement.period_end ? format(new Date(settlement.period_end), "MMM d, yyyy") : "N/A"}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium text-blue-600">
                      +฿{settlement.total_deposits?.toLocaleString() || 0}
                    </TableCell>
                    <TableCell className="text-right font-medium text-emerald-600">
                      -฿{settlement.total_withdrawals?.toLocaleString() || 0}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-bold text-slate-900">
                        ฿{settlement.net_amount?.toLocaleString() || 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${status.color} gap-1 border-0`}>
                        <StatusIcon className={`h-3 w-3 ${settlement.status === "processing" ? "animate-spin" : ""}`} />
                        {settlement.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setSelectedSettlement(settlement)}>
                            <Eye className="h-4 w-4 mr-2" />
                            {t("viewDetails")}
                          </DropdownMenuItem>
                          {settlement.status === "pending" && (
                            <DropdownMenuItem onClick={() => handleProcess(settlement)}>
                              <Send className="h-4 w-4 mr-2" />
                              {t("process")}
                            </DropdownMenuItem>
                          )}
                          {settlement.status === "processing" && (
                            <DropdownMenuItem onClick={() => handleComplete(settlement)}>
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              {t("markCompleted")}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem>
                            <Download className="h-4 w-4 mr-2" />
                            {t("exportReport")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </motion.tr>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Settlement Dialog */}
      <CreateSettlementDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        merchants={merchants}
        onCreate={(data) => createMutation.mutate(data)}
      />
    </div>
  );
}

function CreateSettlementDialog({ open, onClose, merchants, onCreate }) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    merchant_id: "",
    period_start: "",
    period_end: "",
    total_deposits: 0,
    total_withdrawals: 0,
    total_fees: 0,
    net_amount: 0,
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  React.useEffect(() => {
    const net = (formData.total_deposits || 0) - (formData.total_withdrawals || 0) - (formData.total_fees || 0);
    setFormData((prev) => ({ ...prev, net_amount: net }));
  }, [formData.total_deposits, formData.total_withdrawals, formData.total_fees]);

  const handleSubmit = async () => {
    setSaving(true);
    const settlementId = "STL" + Date.now().toString().slice(-8);
    const merchant = merchants.find((m) => m.id === formData.merchant_id);

    await onCreate({
      settlement_id: settlementId,
      merchant_id: formData.merchant_id,
      merchant_code: merchant?.code,
      period_start: formData.period_start,
      period_end: formData.period_end,
      status: "pending",
      total_deposits: parseFloat(formData.total_deposits) || 0,
      total_withdrawals: parseFloat(formData.total_withdrawals) || 0,
      total_fees: parseFloat(formData.total_fees) || 0,
      net_amount: formData.net_amount,
      currency: "THB",
    });

    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("createSettlement")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>{t("merchant")} *</Label>
            <Select
              value={formData.merchant_id}
              onValueChange={(v) => handleChange("merchant_id", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("selectMerchant")} />
              </SelectTrigger>
              <SelectContent>
                {merchants.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} ({m.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("periodStart")} *</Label>
              <Input
                type="date"
                value={formData.period_start}
                onChange={(e) => handleChange("period_start", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("periodEnd")} *</Label>
              <Input
                type="date"
                value={formData.period_end}
                onChange={(e) => handleChange("period_end", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("totalDeposits")} (THB)</Label>
            <Input
              type="number"
              value={formData.total_deposits}
              onChange={(e) => handleChange("total_deposits", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t("totalWithdrawals")} (THB)</Label>
            <Input
              type="number"
              value={formData.total_withdrawals}
              onChange={(e) => handleChange("total_withdrawals", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t("totalFees")} (THB)</Label>
            <Input
              type="number"
              value={formData.total_fees}
              onChange={(e) => handleChange("total_fees", e.target.value)}
            />
          </div>

          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-medium">{t("netAmount")}:</span>
              <span className="text-xl font-bold text-slate-900">
                ฿{formData.net_amount.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !formData.merchant_id || !formData.period_start || !formData.period_end}
          >
            {saving ? t("creating") : t("createPayout")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}